import React, { useMemo, useState } from 'react';
import { Search, X, Check, Trash2, Plus } from 'lucide-react';

const INPUT =
  'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';
const LABEL = 'block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300';
const STEP = 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2';

const AppLogo = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt={name} className="w-9 h-9 rounded-lg object-contain bg-white ring-1 ring-gray-200 dark:ring-white/10" onError={() => setFailed(true)} />;
  }
  return (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-sm font-bold text-gray-400">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

// The type an app was saved under: its template type, else one matching by name, else the typed-in type
const initialType = (initial, toolTypes) => {
  if (!initial) return null;
  const match = toolTypes.find((t) => t.id === initial.baseToolId) || toolTypes.find((t) => sameName(t.name, initial.toolName));
  if (match) return match;
  return initial.toolName ? { id: null, name: initial.toolName, software: [], inStack: [] } : null;
};

/**
 * "+" on a pyramid tier: add an app to the client's stack (or edit/remove one
 * added earlier). Step 1 picks the tool type — one of the tier's template types
 * (to run a second app beside it, like two AIs) or a new type typed in. Step 2
 * picks that type's app, or types any name.
 * toolTypes: tierToolTypes() — [{ id, name, software, inStack }].
 * onSave({ name, toolName, baseToolId, open, library }) — library is the template software or null.
 */
const AddTechStackAppModal = ({ mode = 'add', tierTitle, initial = null, toolTypes = [], saving = false, onClose, onSave, onRemove }) => {
  const [type, setType] = useState(() => initialType(initial, toolTypes));
  const [typeQuery, setTypeQuery] = useState('');
  const [picked, setPicked] = useState(() => {
    const t = initialType(initial, toolTypes);
    return initial?.libraryName ? t?.software.find((sw) => sw.name === initial.libraryName) || null : null;
  });
  const [name, setName] = useState(initial && !initial.libraryName ? initial.name || '' : '');
  const [open, setOpen] = useState(!!initial?.open);

  const typeResults = useMemo(() => {
    const q = typeQuery.trim().toLowerCase();
    return q ? toolTypes.filter((t) => t.name.toLowerCase().includes(q)) : toolTypes;
  }, [typeQuery, toolTypes]);
  const typedNewType = typeQuery.trim() && !toolTypes.some((t) => sameName(t.name, typeQuery)) ? typeQuery.trim() : '';

  // Names already on the pyramid for this type (the app being edited doesn't count)
  const taken = (appName) => (type?.inStack || []).some((n) => sameName(n, appName) && !sameName(n, initial?.name));

  const chooseType = (t) => {
    setType(t);
    setPicked(null);
    setName('');
    setTypeQuery('');
  };

  const pickApp = (sw) => {
    setPicked(sw);
    setName('');
    setOpen(!!sw.open);
  };

  const appName = picked ? picked.name : name.trim();
  const canSave = type && appName && !taken(appName) && !saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onMouseDown={onClose}>
      <div
        className="relative w-full max-w-lg max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="px-6 pt-6 pb-4 text-center border-b border-gray-100 dark:border-gray-800">
          <div className="text-3xl mb-2" aria-hidden="true">{mode === 'add' ? '➕' : '🧩'}</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {mode === 'add' ? `Add an app to ${tierTitle}` : `Edit ${initial?.name || 'app'}`}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Pick the tool type first, then the app. Pick a type twice when they run two — like two AIs.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Step 1 — tool type */}
          <div>
            <div className={STEP}>1 · Tool type</div>
            {type ? (
              <div className="flex items-center gap-3 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/20 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">{type.name}</div>
                  <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {type.id
                      ? type.inStack.length
                        ? `Already using ${type.inStack.join(', ')}`
                        : `${tierTitle} tool type`
                      : 'New tool type — just this company'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => chooseType(null)}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={typeQuery}
                    onChange={(e) => setTypeQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      if (typeResults.length === 1) chooseType(typeResults[0]);
                      else if (typedNewType) chooseType({ id: null, name: typedNewType, software: [], inStack: [] });
                    }}
                    placeholder="Search tool types or type a new one…"
                    className={`${INPUT} pl-10`}
                    autoFocus
                  />
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                  {typeResults.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => chooseType(t)}
                      className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                    >
                      <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{t.name}</span>
                      <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">
                        {t.inStack.length ? `Using ${t.inStack.join(', ')}` : `${t.software.length} app${t.software.length === 1 ? '' : 's'}`}
                      </span>
                    </button>
                  ))}
                </div>
                {typedNewType && (
                  <button
                    type="button"
                    onClick={() => chooseType({ id: null, name: typedNewType, software: [], inStack: [] })}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-red-300 hover:text-red-600 transition"
                  >
                    <Plus className="w-4 h-4" /> Add “{typedNewType}” as a new tool type
                  </button>
                )}
                {!typeResults.length && !typedNewType && (
                  <p className="mt-2 text-sm text-center text-gray-500 dark:text-gray-400">Type a name to add a new tool type.</p>
                )}
              </>
            )}
          </div>

          {/* Step 2 — the app */}
          {type && (
            <div>
              <div className={STEP}>2 · App</div>
              {type.software.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                  {type.software.map((sw) => {
                    const on = picked?.name === sw.name;
                    const inUse = taken(sw.name);
                    return (
                      <button
                        key={sw.id || sw.name}
                        type="button"
                        disabled={inUse}
                        onClick={() => pickApp(sw)}
                        className={`flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition disabled:opacity-50 disabled:cursor-not-allowed ${
                          on
                            ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <AppLogo src={sw.image} name={sw.name} />
                        <span className="flex-1 min-w-0">
                          <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{sw.name}</span>
                          {inUse && <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">Already in their stack</span>}
                        </span>
                        {on && <Check className="w-4 h-4 text-red-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className={type.software.length ? 'mt-4' : ''}>
                <label className={LABEL}>{type.software.length ? 'Not listed? Type the app name' : 'App name'}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setPicked(null);
                  }}
                  placeholder={type.software.length ? 'Any other app' : 'e.g. QuickBooks'}
                  className={INPUT}
                  autoFocus={!type.software.length}
                />
                {!picked && name.trim() && taken(name) && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{name.trim()} is already in their {type.name}.</p>
                )}
              </div>
              {!picked && (
                <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                  Has an Open API
                </label>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center gap-2">
          {mode === 'edit' && onRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={saving}
              className="mr-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 dark:border-red-900/60 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 text-sm font-medium"
            >
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => onSave({ name: appName, toolName: type.name, baseToolId: type.id, open: picked ? !!picked.open : open, library: picked })}
            className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold"
          >
            {saving ? 'Saving…' : mode === 'add' ? 'Add to stack' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTechStackAppModal;
